/**
 * Error Handler Utility
 * Centralized error handling and user-friendly error messages
 */

import { message as antdMessage } from 'antd';

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: any;
}

/**
 * Extract error message from different error formats
 */
export const getErrorMessage = (error: any): string => {
  // Axios error response
  if (error.response?.data) {
    const data = error.response.data;

    // Check for common error message patterns
    if (typeof data === 'string') return data;
    if (data.message) return data.message;
    if (data.error) return data.error;
    if (data.detail) return data.detail;
    if (data.errors) {
      // Handle validation errors
      if (Array.isArray(data.errors)) {
        return data.errors.map((err: any) => err.message || err).join(', ');
      }
      if (typeof data.errors === 'object') {
        return Object.values(data.errors).flat().join(', ');
      }
    }
  }

  // Network or timeout errors
  if (error.code === 'ECONNABORTED') {
    return 'Request timeout. Please try again.';
  }
  if (error.code === 'ERR_NETWORK') {
    return 'Network error. Please check your connection.';
  }
  if (error.message === 'Network Error') {
    return 'Unable to connect to server. Please check your network connection.';
  }

  // Generic error message
  if (error.message) return error.message;

  return 'An unexpected error occurred. Please try again.';
};

/**
 * Get HTTP status code from error
 */
export const getErrorStatus = (error: any): number | undefined => {
  return error.response?.status;
};

/**
 * Get error code from error
 */
export const getErrorCode = (error: any): string | undefined => {
  return error.response?.data?.code || error.code;
};

/**
 * Format API error into ApiError interface
 */
export const formatApiError = (error: any): ApiError => {
  return {
    message: getErrorMessage(error),
    status: getErrorStatus(error),
    code: getErrorCode(error),
    details: error.response?.data,
  };
};

/**
 * Display error message to user using Ant Design message
 */
export const showErrorMessage = (error: any, customMessage?: string): void => {
  const errorMessage = customMessage || getErrorMessage(error);
  antdMessage.error(errorMessage, 5); // Show for 5 seconds
};

/**
 * Display success message to user
 */
export const showSuccessMessage = (message: string): void => {
  antdMessage.success(message, 3);
};

/**
 * Display warning message to user
 */
export const showWarningMessage = (message: string): void => {
  antdMessage.warning(message, 3);
};

/**
 * Display info message to user
 */
export const showInfoMessage = (message: string): void => {
  antdMessage.info(message, 3);
};

/**
 * HTTP Status code messages
 */
export const HTTP_STATUS_MESSAGES: { [key: number]: string } = {
  400: 'Bad Request. Please check your input.',
  401: 'Unauthorized. Please login again.',
  403: 'Access forbidden. You don\'t have permission.',
  404: 'Resource not found.',
  408: 'Request timeout. Please try again.',
  409: 'Conflict. The resource already exists.',
  422: 'Validation error. Please check your input.',
  429: 'Too many requests. Please slow down.',
  500: 'Internal server error. Please try again later.',
  502: 'Bad gateway. Server is unavailable.',
  503: 'Service unavailable. Please try again later.',
  504: 'Gateway timeout. Please try again.',
};

/**
 * Get user-friendly message for HTTP status code
 */
export const getStatusMessage = (status: number): string => {
  return HTTP_STATUS_MESSAGES[status] || 'An error occurred. Please try again.';
};

export default {
  getErrorMessage,
  getErrorStatus,
  getErrorCode,
  formatApiError,
  showErrorMessage,
  showSuccessMessage,
  showWarningMessage,
  showInfoMessage,
  getStatusMessage,
};
